import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Languages, Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE } from '../config/api';
const API = `${API_BASE}`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [debugToken, setDebugToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/auth/forgot-password`, { email });
      setSubmitted(true);
      // For testing: show debug token (REMOVE IN PRODUCTION)
      if (response.data.debug_token) {
        setDebugToken(response.data.debug_token);
      }
      // Log debug URL to console for development
      if (response.data.debug_reset_url) {
        console.log('Debug reset URL:', response.data.debug_reset_url);
      }
      toast.success('If an account exists, a reset link has been sent');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 noise-overlay">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="forgot-password-logo">
            <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center">
              <Languages className="w-6 h-6 text-primary" />
            </div>
            <span className="font-heading text-2xl font-semibold text-foreground">
              <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
            </span>
          </Link>

          <Card className="bg-background-paper border-border" data-testid="forgot-password-success-card">
            <CardContent className="pt-6 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-quaternary/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-quaternary" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-foreground">Check Your Email</h2>
              <p className="text-foreground-muted">
                If an account exists for <span className="text-foreground font-medium">{email}</span>, 
                you'll receive a password reset link shortly.
              </p>
              
              {/* Debug token display (REMOVE IN PRODUCTION) */}
              {debugToken && (
                <div className="p-4 bg-background border border-border rounded-md text-left">
                  <p className="text-xs text-foreground-subtle mb-2 font-mono">DEBUG: Reset Token</p>
                  <p className="text-sm text-secondary font-mono break-all">{debugToken}</p>
                  <Link 
                    to={`/reset-password?token=${debugToken}`}
                    className="text-sm text-primary hover:underline block mt-2"
                    data-testid="debug-reset-link"
                  >
                    Click here to reset password
                  </Link>
                </div>
              )}
              
              <Link to="/login">
                <Button variant="outline" className="border-border hover:bg-accent" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 noise-overlay">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="forgot-password-logo">
          <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center">
            <Languages className="w-6 h-6 text-primary" />
          </div>
          <span className="font-heading text-2xl font-semibold text-foreground">
            <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
          </span>
        </Link>

        <Card className="bg-background-paper border-border" data-testid="forgot-password-card">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-2xl text-foreground">Forgot Password</CardTitle>
            <CardDescription className="text-foreground-muted">
              Enter your email and we'll send you a reset link
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-background border-border focus:ring-primary"
                    required
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="forgot-password-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>

            <Link 
              to="/login" 
              className="flex items-center justify-center gap-2 text-sm text-foreground-muted hover:text-foreground"
              data-testid="forgot-password-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
